<template>
  <IdentityCharacterValidation
    v-if="showValidation"
    @next="($event) => sendValidationResult($event)"
    @setLoading="(isLoading) => setLoading(isLoading)"
    @onValidationFailed="sendValidationFailed"
    :validation-id="requireValidationResponse?.requiredValidations?.[0]?.id"
    :customerName="customerName"
    :hide-close-button="hideCloseButton"
    :validationConfig="validationConfig"
    :userFlow="userFlow"
  />
  <LoadingOverlay :loading="loading" />
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import IdentityCharacterValidation from "@/apps/credit-line/components/KycValidations/IdentityCharacterValidation.vue";
import LoadingOverlay from "@/shared-components/ui/core-app/LoadingOverlay.vue";
import {
  AreValidationsRequiredResponse,
  NationalIdValidationResponse,
} from "@/apps/credit-line/models/IKycValidations";
import { getCountry } from "@/apps/credit-line/helpers/translations.helper";
import {
  FeatureControlService,
  AvailableFeatures,
} from "@/apps/credit-line/services/feature-control.service";
import {
  SettingValue,
  DEFAULT_SETTING,
} from "@/shared-models/consumer/ISetting";
import { getSettings } from "@/apps/credit-line/helpers/consumerSettings.helper";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { KycUserFlow } from "../../enums/kycEnums";
import { SettingsCategory } from "@/shared-models/consumer/SettingsCategory";

interface ValidationConfig {
  validationType: string;
  documentName: string;
  image: string;
  whatsAppNumber: string;
}

const props = defineProps<{
  customerName: string;
  requireValidationResponse: AreValidationsRequiredResponse;
  hideCloseButton: boolean;
  userFlow?: KycUserFlow;
}>();

const emit = defineEmits<{
  (e: "next", result: NationalIdValidationResponse): void;
  (e: "setScreenTitle", title: string): void;
  (e: "setLoading", isLoading: boolean): void;
  (e: "onValidationFailed"): void;
}>();

const { t } = useI18n();
const featureControl = new FeatureControlService();

const loading = ref<boolean>(false);
const identityValidationSetting = ref<SettingValue>(DEFAULT_SETTING);
const whatsAppSupportNumber = ref<SettingValue>(DEFAULT_SETTING);
const calculatedCountry = ref<string>("MX");
const fallbackConfig = ref<ValidationConfig | null>(null);

// Initialize identity validation settings
const initializeSettings = async () => {
  try {
    const settings = await getSettings(SettingsCategory.CL_CARDS_PAGE);
    identityValidationSetting.value =
      settings["identity_validation"] || DEFAULT_SETTING;
    whatsAppSupportNumber.value =
      settings["whatsAppSupportNumber"] || DEFAULT_SETTING;

    // If settings service is unavailable, prepare fallback configuration
    if (!identityValidationSetting.value?.enabled) {
      await initializeFallbackConfig();
    }
  } catch (error) {
    console.error("Failed to load identity validation settings:", error);
    // Initialize fallback on error
    await initializeFallbackConfig();
  }
};

// Initialize fallback configuration when settings service is unavailable
const initializeFallbackConfig = async () => {
  try {
    // Get country asynchronously
    const country = (await getCountry()) || "MX";
    calculatedCountry.value = country;

    const fallbackWhatsAppNumber =
      featureControl.getFeatureValue(
        AvailableFeatures.WHATSAPP,
        country,
        "number"
      ) || "+525575028669";

    const fallbackValidationType = String(
      featureControl.getFeatureValue(
        AvailableFeatures.IDENTITY_VALIDATION,
        country,
        "validation_type"
      )
    );
    const fallbackDocumentName = String(
      featureControl.getFeatureValue(
        AvailableFeatures.IDENTITY_VALIDATION,
        country,
        "document_name"
      )
    );
    const fallbackValidationImage = String(
      featureControl.getFeatureValue(
        AvailableFeatures.IDENTITY_VALIDATION,
        country,
        "validation_image"
      )
    );

    fallbackConfig.value = {
      validationType: fallbackValidationType,
      documentName: fallbackDocumentName,
      image: useAsset(fallbackValidationImage),
      whatsAppNumber: String(fallbackWhatsAppNumber),
    };
  } catch (error) {
    console.error("Failed to initialize fallback configuration:", error);
    // Use hardcoded fallback as last resort
    fallbackConfig.value = {
      validationType: "curp_four_last_characters",
      documentName: "CURP",
      image: useAsset("curp_validation_image.png"),
      whatsAppNumber: "+525575028669",
    };
  }
};

// Computed validation configuration - no async operations
const validationConfig = computed(() => {
  const settings = identityValidationSetting.value;
  const whatsAppSettings = whatsAppSupportNumber.value;

  // Get WhatsApp number from settings or fallback
  const whatsAppNumber =
    whatsAppSettings?.enabled && whatsAppSettings.values?.phone
      ? String(whatsAppSettings.values.phone)
      : "+525575028669"; // Fallback to Mexico number

  // Try to use settings service first
  if (settings?.enabled && settings.values) {
    return {
      validationType: String(settings.values.validationType),
      documentName: String(settings.values.documentName),
      image: useAsset(String(settings.values.validationImage)),
      whatsAppNumber: whatsAppNumber,
    };
  }

  // Use pre-initialized fallback configuration if available
  if (fallbackConfig.value) {
    return fallbackConfig.value;
  }

  // Last resort - hardcoded fallback while fallback config is loading
  return {
    validationType: "curp_four_last_characters",
    documentName: "CURP",
    image: useAsset("curp_validation_image.png"),
    whatsAppNumber: "+525575028669",
  };
});

// Show validation component (in future could be conditional based on validation type)
const showValidation = computed(() => {
  return props?.requireValidationResponse?.requiredValidations?.length > 0;
});

// Methods
function setLoading(isLoading: boolean) {
  loading.value = isLoading;
}

function sendValidationResult(result: NationalIdValidationResponse) {
  emit("next", result);
}

function sendValidationFailed() {
  emit("onValidationFailed");
}

onMounted(async () => {
  await initializeSettings();
  emit("setScreenTitle", t("CL_WITHDRAWAL.IDENTITY_VERIFICATION"));
});
</script>
